import { FC, PropsWithChildren } from 'react';

import { IconButton, Palette, styled } from '@mui/material';

import { TFunction } from 'i18next';
import {
  MaterialDesignContent,
  SnackbarProvider as NotistackProvider,
  SnackbarKey,
  closeSnackbar,
} from 'notistack';

import useLocales from '@src/locales/use-locales';

import Iconify from '../iconify';

const StyledIcon = styled('span')<{
  color: keyof Palette['custom'];
}>(({ color, theme }) => {
  const colorVar = theme.palette.custom[color];
  return {
    width: 22,
    height: 22,
    marginRight: theme.spacing(1.5),
    color: colorVar,
  };
});

export const StyledNotistack = styled(MaterialDesignContent)(({ theme }) => {
  return {
    '& #notistack-snackbar': {
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: theme.typography.fontWeightMedium,
      alignItems: 'center',
      padding: 0,
      flex: 1,
    },
    '&.notistack-MuiContent': {
      padding: theme.spacing(0),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
    },
    '&.notistack-MuiContent-info': {
      color: '#014361',
      backgroundColor: theme.palette.custom.info,
    },
    '&.notistack-MuiContent-success': {
      color: '#1E4620',
      backgroundColor: theme.palette.custom.success,
    },
    '&.notistack-MuiContent-warning': {
      color: '#663C00',
      backgroundColor: theme.palette.custom.warning,
    },
    '&.notistack-MuiContent-error': {
      color: '#5F2120',
      backgroundColor: theme.palette.custom.error,
    },
  };
});

const renderAction = (snackbarId: SnackbarKey, t: TFunction) => (
  <IconButton
    size="small"
    onClick={() => closeSnackbar(snackbarId)}
    sx={{ p: 0.5 }}
    aria-label={t('ariaLabels.closeSnackbar')}
  >
    <Iconify width={16} icon="material-symbols-light:close" />
  </IconButton>
);

type SnackbarProviderProps = PropsWithChildren;

const SnackbarProvider: FC<SnackbarProviderProps> = (props) => {
  const { children } = props;
  const { t } = useLocales();

  return (
    <NotistackProvider
      maxSnack={3}
      preventDuplicate
      autoHideDuration={3000}
      variant="success"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      iconVariant={{
        info: (
          <StyledIcon color="blue">
            <Iconify icon="ic:outline-info" size={22} />
          </StyledIcon>
        ),
        success: (
          <StyledIcon color="green">
            <Iconify icon="ic:baseline-check-circle-outline" size={22} />
          </StyledIcon>
        ),
        warning: (
          <StyledIcon color="orange">
            <Iconify icon="ic:baseline-warning-amber" size={22} />
          </StyledIcon>
        ),
        error: (
          <StyledIcon color="red">
            <Iconify icon="ic:baseline-error-outline" size={22} />
          </StyledIcon>
        ),
      }}
      action={(id) => renderAction(id, t)}
      Components={{
        default: StyledNotistack,
        info: StyledNotistack,
        success: StyledNotistack,
        warning: StyledNotistack,
        error: StyledNotistack,
      }}
    >
      {children}
    </NotistackProvider>
  );
};

export default SnackbarProvider;
